import type { FC } from "preact/compat";
import { useEffect, useId, useRef } from "preact/hooks";
import { useComputed, useSignal, useSignalEffect } from "@preact/signals";
import { Animation, animator, useAnimator } from "~/chart";
import { linear } from "~/chart/anim/timing";
import { lerp } from "~/chart/utils";
import { LogoLoaderProps, Point } from "./interfaces";
import {
    alignPoints,
    angleToPoint,
    assignClosestTargets,
    buildCirclePoints,
    circlePath,
    interpolatePointSets,
    normalizeSigned,
    pointToAngle,
    pointsToPath,
    samplePathPoints,
} from "./utils";

const DOT_INIT_SIZE = 3.75;
const DOT_MAX_SIZE = 6;
const DOT_INIT_HALF = DOT_INIT_SIZE / 2;
const DOT_MAX_HALF = DOT_MAX_SIZE / 2;
const WIDTH = 39;
const HEIGHT = 39;
const PADDING = 24;
const VB_PADDING = DOT_MAX_HALF + 1;
const VB_X = -VB_PADDING;
const VB_Y = -12 - VB_PADDING;
const VB_WIDTH = WIDTH + VB_PADDING * 2;
const VB_HEIGHT = HEIGHT + VB_PADDING * 2;
const CENTER_X = WIDTH / 2;
const CENTER_Y = 7.5;
const INNER_CIRCLE_RADIUS = 3.5;
const ORBIT_GAP = 8;
const ORBIT_RADIUS = INNER_CIRCLE_RADIUS + ORBIT_GAP + DOT_INIT_HALF;
const SPIN_DURATION = 1200;
const ORBIT_EXIT_DURATION = 400;
const RETURN_INNER_ARC_BEND = 0.45;
const PULSE_DURATION = 900;
const PULSE_SCALE_MIN = 0.94;
const PULSE_SCALE_MAX = 1.06;

const DOT_1_TARGET = {
    x: DOT_INIT_HALF,
    y: HEIGHT - PADDING - DOT_INIT_HALF,
};

const DOT_2_TARGET = {
    x: WIDTH - DOT_INIT_HALF,
    y: DOT_INIT_HALF,
};

const DOT_3_TARGET = {
    x: 18,
    y: DOT_INIT_HALF / 2 + DOT_INIT_HALF - 1,
};

const DOT_4_TARGET = {
    x: 20,
    y: HEIGHT - PADDING - DOT_INIT_HALF,
};

const TWO_PI = Math.PI * 2;

const MAIN_LOGO_PATH =
    "M38.571 1.87495C38.571 0.839433 37.6135 0 36.4325 0H33.9599H21.1289H19.2746C17.7498 0 16.3136 0.629575 15.3987 1.69953L8.68289 9.55057C7.76798 10.6205 6.33184 11.2501 4.80653 11.2501H2.13849C0.957424 11.2501 0 12.0895 0 13.1251C0 14.1606 0.957424 15 2.13849 15H4.02146H16.8524H18.7067C20.232 15 21.6682 14.3704 22.5831 13.3005L29.2989 5.44943C30.2138 4.37947 31.65 3.7499 33.1753 3.7499H36.4329C37.614 3.7499 38.5714 2.91047 38.5714 1.87495H38.571Z";

const MAIN_LOADING_CIRCLE_PATH = circlePath(
    CENTER_X,
    CENTER_Y,
    INNER_CIRCLE_RADIUS,
);
const MORPH_DURATION = 500;
const MORPH_POINTS = 160;

const INITIAL_DOT_TARGETS = {
    dot1: DOT_1_TARGET,
    dot2: DOT_2_TARGET,
    dot3: DOT_3_TARGET,
    dot4: DOT_4_TARGET,
};

const resolveDotPosition = (
    target: { x: number; y: number },
    orbitPoint: { x: number; y: number },
    orbitAngle: number,
    loading: boolean,
    orbitProgress: number,
) => {
    if (!loading) {
        const returnProgress = 1 - orbitProgress;

        if (returnProgress <= 0) {
            return orbitPoint;
        }

        const targetAngle = pointToAngle(target, CENTER_X, CENTER_Y);
        const targetRadius = Math.hypot(
            target.x - CENTER_X,
            target.y - CENTER_Y,
        );
        const angularTravel = normalizeSigned(targetAngle - orbitAngle);
        const bendDirection =
            Math.abs(angularTravel) < 1e-4 ? 1 : Math.sign(angularTravel);
        const bend =
            bendDirection *
            RETURN_INNER_ARC_BEND *
            Math.sin(Math.PI * returnProgress);
        const angle = orbitAngle + angularTravel * returnProgress + bend;
        const radius = lerp(ORBIT_RADIUS, targetRadius, returnProgress);

        return angleToPoint(angle, CENTER_X, CENTER_Y, radius);
    }

    return {
        x: lerp(target.x, orbitPoint.x, orbitProgress),
        y: lerp(target.y, orbitPoint.y, orbitProgress),
    };
};

const LogoLoader: FC<LogoLoaderProps> = ({
    loading,
    width = WIDTH,
    className,
}) => {
    const myId = useId();
    const morphProgress = useSignal(loading.peek() ? 1 : 0);
    const pulseScale = useSignal(1);
    const morphAnimationRef = useRef<Animation | null>(null);
    const pulseAnimationRef = useRef<Animation | null>(null);
    const logoPointsRef = useRef<Point[]>([]);
    const circlePointsRef = useRef<Point[]>([]);
    const morphFromPointsRef = useRef<Point[]>([]);
    const morphToPointsRef = useRef<Point[]>([]);
    const morphCurrentPointsRef = useRef<Point[]>([]);
    const orbitProgress = useSignal(loading.peek() ? 1 : 0);
    const rotationTurns = useSignal(0);
    const dotTargets = useSignal(INITIAL_DOT_TARGETS);
    const trackTagRef = useRef(`logo-${myId}`);
    const orbitAnimationRef = useRef<Animation | null>(null);
    const spinAnimationRef = useRef<Animation | null>(null);
    const isSpinningRef = useRef(false);
    const isPulsingRef = useRef(false);
    const pulseToMaxRef = useRef(true);
    const morphPath = useComputed(() => {
        const ld = loading.value;
        const mp = morphProgress.value;
        const fromPoints = morphFromPointsRef.current;
        const toPoints = morphToPointsRef.current;

        if (fromPoints.length === 0 || toPoints.length === 0) {
            return ld ? MAIN_LOADING_CIRCLE_PATH : MAIN_LOGO_PATH;
        }

        const points = interpolatePointSets(fromPoints, toPoints, mp);

        morphCurrentPointsRef.current = points;

        return pointsToPath(points, MAIN_LOGO_PATH);
    });
    const transform = useComputed(() => {
        const ld = loading.value;
        const mp = morphProgress.value;
        const ps = pulseScale.value;
        const sp = ld ? ps : lerp(1, ps, mp);

        return `translate(${CENTER_X} ${CENTER_Y}) scale(${sp}) translate(${-CENTER_X} ${-CENTER_Y})`;
    });

    useAnimator();

    useEffect(() => {
        const trackTag = trackTagRef.current;

        animator.track(trackTag);

        const morphAnimation = animator.anim(
            morphProgress.peek(),
            MORPH_DURATION,
        );
        const pulseAnimation = animator.anim(1, PULSE_DURATION);
        const orbitAnimation = animator.anim(orbitProgress.peek(), 300);
        const spinAnimation = animator.anim(0, SPIN_DURATION);

        morphAnimation.onUpdate((v) => (morphProgress.value = v));
        pulseAnimation.onUpdate((v) => (pulseScale.value = v));
        orbitAnimation.onUpdate((v) => (orbitProgress.value = v));
        spinAnimation.onUpdate((v) => (rotationTurns.value = v));

        if (logoPointsRef.current.length === 0) {
            logoPointsRef.current = samplePathPoints(
                MAIN_LOGO_PATH,
                MORPH_POINTS,
            );
            circlePointsRef.current = alignPoints(
                logoPointsRef.current,
                buildCirclePoints(
                    MORPH_POINTS,
                    CENTER_X,
                    CENTER_Y,
                    INNER_CIRCLE_RADIUS,
                ),
            );
            morphCurrentPointsRef.current = loading.peek()
                ? circlePointsRef.current
                : logoPointsRef.current;
            morphFromPointsRef.current = morphCurrentPointsRef.current;
            morphToPointsRef.current = morphCurrentPointsRef.current;
        }

        morphAnimation.set(loading.peek() ? 1 : 0);
        pulseAnimation.set(1);
        orbitAnimation.set(loading.peek() ? 1 : 0);
        spinAnimation.timingFunction = linear;

        morphAnimationRef.current = morphAnimation;
        pulseAnimationRef.current = pulseAnimation;
        orbitAnimationRef.current = orbitAnimation;
        spinAnimationRef.current = spinAnimation;

        animator.untrack(trackTag);

        return () => {
            animator.clean(trackTag);
            morphAnimationRef.current = null;
            pulseAnimationRef.current = null;
            orbitAnimationRef.current = null;
            spinAnimationRef.current = null;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useSignalEffect(() => {
        const val = loading.value;
        const morphAnimation = morphAnimationRef.current;
        const logoPoints = logoPointsRef.current;
        const circlePoints = circlePointsRef.current;

        if (!morphAnimation || logoPoints.length === 0) {
            return;
        }

        morphFromPointsRef.current = morphCurrentPointsRef.current;
        morphToPointsRef.current = val ? circlePoints : logoPoints;
        morphAnimation.duration = MORPH_DURATION;
        morphAnimation.set(0);
        morphProgress.value = 0;
        morphAnimation.play(1);
        animator.forceUpdate();
    });

    useSignalEffect(() => {
        const val = loading.value;
        const pulseAnimation = pulseAnimationRef.current;

        if (!pulseAnimation) {
            return;
        }

        if (val) {
            if (!isPulsingRef.current) {
                isPulsingRef.current = true;
                pulseToMaxRef.current = pulseAnimation.value <= 1;
                pulseAnimation.duration = PULSE_DURATION;
                pulseAnimation.onEnd = () => {
                    if (!isPulsingRef.current) {
                        return;
                    }

                    const nextTarget = pulseToMaxRef.current
                        ? PULSE_SCALE_MAX
                        : PULSE_SCALE_MIN;

                    pulseToMaxRef.current = !pulseToMaxRef.current;
                    pulseAnimation.play(nextTarget);
                };
                const initialTarget = pulseToMaxRef.current
                    ? PULSE_SCALE_MAX
                    : PULSE_SCALE_MIN;

                pulseToMaxRef.current = !pulseToMaxRef.current;
                pulseAnimation.play(initialTarget);
            }
        } else {
            isPulsingRef.current = false;
            pulseAnimation.onEnd = undefined;
            pulseAnimation.duration = 260;
            pulseAnimation.play(1);
        }

        animator.forceUpdate();
    });

    useSignalEffect(() => {
        const val = loading.value;
        const orbitAnimation = orbitAnimationRef.current;
        const spinAnimation = spinAnimationRef.current;

        if (!orbitAnimation || !spinAnimation) {
            return;
        }

        if (val) {
            orbitAnimation.duration = 300;
            orbitAnimation.play(1);

            if (!isSpinningRef.current) {
                isSpinningRef.current = true;
                spinAnimation.duration = SPIN_DURATION;
                spinAnimation.timingFunction = linear;
                spinAnimation.onEnd = () => {
                    if (!isSpinningRef.current) {
                        return;
                    }

                    spinAnimation.play(spinAnimation.value + 1);
                };
                spinAnimation.play(spinAnimation.value + 1);
            }
        } else if (isSpinningRef.current) {
            isSpinningRef.current = false;
            spinAnimation.onEnd = undefined;

            const stopTurns = spinAnimation.value;

            spinAnimation.stop();
            rotationTurns.value = stopTurns;

            const stopTheta = stopTurns * TWO_PI - Math.PI / 2;
            const sd1 = stopTheta;
            const sd2 = stopTheta + Math.PI;
            const sd3 = stopTheta + Math.PI / 2;
            const sd4 = stopTheta + (3 * Math.PI) / 2;
            const stopPositions = [
                angleToPoint(sd1, CENTER_X, CENTER_Y, ORBIT_RADIUS),
                angleToPoint(sd2, CENTER_X, CENTER_Y, ORBIT_RADIUS),
                angleToPoint(sd3, CENTER_X, CENTER_Y, ORBIT_RADIUS),
                angleToPoint(sd4, CENTER_X, CENTER_Y, ORBIT_RADIUS),
            ];
            const assignedTargets = assignClosestTargets(
                stopPositions,
                [DOT_1_TARGET, DOT_2_TARGET, DOT_3_TARGET, DOT_4_TARGET],
                CENTER_X,
                CENTER_Y,
                [sd1, sd2, sd3, sd4],
                true,
            );

            dotTargets.value = assignedTargets;
            orbitAnimation.duration = ORBIT_EXIT_DURATION;
            orbitAnimation.play(0);
        } else {
            orbitAnimation.duration = ORBIT_EXIT_DURATION;
            orbitAnimation.play(0);
        }

        animator.forceUpdate();
    });

    const ld = loading.value;
    const op = orbitProgress.value;
    const dts = dotTargets.value;
    const rt = rotationTurns.value;

    const theta = rt * Math.PI * 2 - Math.PI / 2;

    const d1a = theta;
    const d2a = theta + Math.PI;
    const d3a = theta + Math.PI / 2;
    const d4a = theta + (3 * Math.PI) / 2;

    const d1o = angleToPoint(d1a, CENTER_X, CENTER_Y, ORBIT_RADIUS);
    const d2o = angleToPoint(d2a, CENTER_X, CENTER_Y, ORBIT_RADIUS);
    const d3o = angleToPoint(d3a, CENTER_X, CENTER_Y, ORBIT_RADIUS);
    const d4o = angleToPoint(d4a, CENTER_X, CENTER_Y, ORBIT_RADIUS);

    const d1 = resolveDotPosition(dts.dot1, d1o, d1a, ld, op);
    const d2 = resolveDotPosition(dts.dot2, d2o, d2a, ld, op);
    const d3 = resolveDotPosition(dts.dot3, d3o, d3a, ld, op);
    const d4 = resolveDotPosition(dts.dot4, d4o, d4a, ld, op);
    const dr = lerp(DOT_INIT_HALF, DOT_MAX_HALF, op);

    const gradId = `logo-gradient-${myId}`;
    const gradUrl = `url(#${gradId})`;

    return (
        <svg
            width={width}
            height={width}
            viewBox={`${VB_X} ${VB_Y} ${VB_WIDTH} ${VB_HEIGHT}`}
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className={className}
        >
            <g transform={transform}>
                <path d={morphPath} fill={gradUrl} />
            </g>
            <circle cx={d1.x} cy={d1.y} r={dr} fill={gradUrl} />
            <circle cx={d2.x} cy={d2.y} r={dr} fill={gradUrl} />
            <circle cx={d3.x} cy={d3.y} r={dr} fill={gradUrl} />
            <circle cx={d4.x} cy={d4.y} r={dr} fill={gradUrl} />
            <defs>
                <linearGradient
                    id={gradId}
                    x1="1.40194"
                    y1="12.7827"
                    x2="34.755"
                    y2="-0.249859"
                    gradientUnits="userSpaceOnUse"
                >
                    <stop stop-color="#35C2FF" />
                    <stop offset="1" stop-color="#FF2E82" />
                </linearGradient>
            </defs>
        </svg>
    );
};

export default LogoLoader;
